import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';
import {
  UserIcon, KeyIcon, BellIcon, ShieldCheckIcon,
  EnvelopeIcon, GlobeAltIcon, CameraIcon,
  CheckCircleIcon, XMarkIcon, InformationCircleIcon
} from '@heroicons/react/24/outline';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { PageHeader } from '../../components/layout';
import {
  Card, CardBody, CardHeader, CardTitle, Button, Input, Select, Avatar,
  Tabs, TabList, Tab, TabPanels, TabPanel, Badge
} from '../../components/ui';
import { useAuthStore } from '../../stores';
import { authService } from '../../services';
import { changeLanguage } from '../../i18n';
import { getEnabledLanguages, getDefaultLanguage } from '../../config/languages';

dayjs.extend(utc);
dayjs.extend(timezone);

// Common timezones
const timezones = [
  { value: 'UTC', label: 'UTC (Coordinated Universal Time)' },
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'Europe/London', label: 'London (GMT)' },
  { value: 'Europe/Paris', label: 'Paris (CET)' },
  { value: 'Europe/Berlin', label: 'Berlin (CET)' },
  { value: 'Asia/Dubai', label: 'Dubai (GST)' },
  { value: 'Asia/Riyadh', label: 'Riyadh (AST)' },
  { value: 'Asia/Kuwait', label: 'Kuwait (AST)' },
  { value: 'Asia/Cairo', label: 'Cairo (EET)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'Asia/Shanghai', label: 'Shanghai (CST)' },
  { value: 'Asia/Hong_Kong', label: 'Hong Kong (HKT)' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEDT)' },
  { value: 'Australia/Melbourne', label: 'Melbourne (AEDT)' }
];

export default function Settings() {
  const { t, i18n } = useTranslation();
  const { user, updateProfile, updateAvatar } = useAuthStore();
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  const [avatarLoading, setAvatarLoading] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [notificationLoading, setNotificationLoading] = useState(false);

  const { register: registerProfile, handleSubmit: handleProfileSubmit, control, formState: { errors: profileErrors } } = useForm({
    defaultValues: {
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      phone: user?.phone || '',
      phoneCountryCode: user?.phoneCountryCode || '',
      language: user?.language || getDefaultLanguage(),
      timezone: user?.timezone || 'UTC'
    }
  });

  const { register: registerPassword, handleSubmit: handlePasswordSubmit, reset: resetPassword, watch, formState: { errors: passwordErrors } } = useForm();
  const newPassword = watch('newPassword');

  const [notificationPrefs, setNotificationPrefs] = useState({
    email: user?.notificationPreferences?.email ?? true,
    push: user?.notificationPreferences?.push ?? true,
    projectUpdates: user?.notificationPreferences?.projectUpdates ?? true,
    taskReminders: user?.notificationPreferences?.taskReminders ?? true,
    clientMessages: user?.notificationPreferences?.clientMessages ?? true
  });

  useEffect(() => {
    if (user) {
      setNotificationPrefs({
        email: user.notificationPreferences?.email ?? true,
        push: user.notificationPreferences?.push ?? true,
        projectUpdates: user.notificationPreferences?.projectUpdates ?? true,
        taskReminders: user.notificationPreferences?.taskReminders ?? true,
        clientMessages: user.notificationPreferences?.clientMessages ?? true
      });
    }
  }, [user]);

  const onProfileSubmit = async (data) => {
    setLoading(true);
    try {
      await updateProfile(data);
      if (data.language !== i18n.language) {
        changeLanguage(data.language);
      }
      toast.success(t('success.updated'));
    } catch (error) {
      toast.error(error.response?.data?.message || t('errors.serverError'));
    } finally {
      setLoading(false);
    }
  };

  const onPasswordSubmit = async (data) => {
    setLoading(true);
    try {
      await authService.changePassword(data.currentPassword, data.newPassword);
      toast.success(t('auth.passwordChanged'));
      resetPassword();
    } catch (error) {
      toast.error(error.response?.data?.message || t('errors.serverError'));
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationChange = async (key, value) => {
    const newPrefs = { ...notificationPrefs, [key]: value };
    setNotificationPrefs(newPrefs);
    setNotificationLoading(true);
    try {
      await updateProfile({ notificationPreferences: newPrefs });
      toast.success(t('success.updated'));
    } catch (error) {
      // Revert on error
      setNotificationPrefs(notificationPrefs);
      toast.error(error.response?.data?.message || t('errors.serverError'));
    } finally {
      setNotificationLoading(false);
    }
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error(t('errors.fileTooBig'));
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setAvatarPreview(reader.result);
    };
    reader.readAsDataURL(file);

    setAvatarLoading(true);
    try {
      await updateAvatar(file);
      toast.success(t('success.updated'));
      setAvatarPreview(null);
    } catch (error) {
      toast.error(error.response?.data?.message || t('errors.serverError'));
      setAvatarPreview(null);
    } finally {
      setAvatarLoading(false);
    }
  };

  const languageOptions = getEnabledLanguages().map(lang => ({
    value: lang.code,
    label: `${lang.flag} ${lang.nativeName}`
  }));

  const phoneCountryCodeOptions = [
    { value: '', label: t('common.select') || 'Select' },
    { value: '+1', label: '+1 (US/CA)' },
    { value: '+44', label: '+44 (UK)' },
    { value: '+33', label: '+33 (France)' },
    { value: '+49', label: '+49 (Germany)' },
    { value: '+20', label: '+20 (Egypt)' },
    { value: '+966', label: '+966 (Saudi Arabia)' },
    { value: '+965', label: '+965 (Kuwait)' },
    { value: '+971', label: '+971 (UAE)' },
    { value: '+81', label: '+81 (Japan)' },
    { value: '+86', label: '+86 (China)' },
    { value: '+65', label: '+65 (Singapore)' },
    { value: '+61', label: '+61 (Australia)' }
  ];

  return (
    <>
      <Helmet>
        <title>{t('settings.title')} - ProCTM</title>
      </Helmet>

      <PageHeader title={t('settings.title')} />

      <div className="w-full">
        <Tabs>
          <TabList className="mb-6">
            <Tab active={activeTab === 'profile'} onClick={() => setActiveTab('profile')}>
              <UserIcon className="w-4 h-4 mr-2" />
              {t('settings.profile')}
            </Tab>
            <Tab active={activeTab === 'account'} onClick={() => setActiveTab('account')}>
              <KeyIcon className="w-4 h-4 mr-2" />
              {t('settings.account')}
            </Tab>
            <Tab active={activeTab === 'notifications'} onClick={() => setActiveTab('notifications')}>
              <BellIcon className="w-4 h-4 mr-2" />
              {t('settings.notifications')}
            </Tab>
            <Tab active={activeTab === 'security'} onClick={() => setActiveTab('security')}>
              <ShieldCheckIcon className="w-4 h-4 mr-2" />
              {t('settings.security') || 'Security'}
            </Tab>
          </TabList>

          <TabPanels>
            {/* Profile Tab */}
            <TabPanel active={activeTab === 'profile'}>
              <div className="space-y-6">
                {/* Profile Picture */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <CameraIcon className="w-5 h-5 text-primary" />
                      {t('settings.profilePicture') || 'Profile Picture'}
                    </CardTitle>
                  </CardHeader>
                  <CardBody>
                    <div className="flex items-center gap-6">
                      <div className="relative group">
                        <Avatar
                          src={avatarPreview || (user?.avatar ? (user.avatar.startsWith('http') ? user.avatar : process.env.REACT_APP_SOCKET_URL + user.avatar) : null)}
                          name={`${user?.firstName} ${user?.lastName}`}
                          size="xl"
                          className="ring-4 ring-gray-200 group-hover:ring-primary transition-all"
                        />
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 rounded-full flex items-center justify-center transition-opacity cursor-pointer">
                          <CameraIcon className="w-6 h-6 text-white" />
                        </div>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleAvatarChange}
                          className="absolute inset-0 opacity-0 cursor-pointer"
                          disabled={avatarLoading}
                        />
                        {avatarLoading && (
                          <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                          </div>
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-lg">{user?.firstName} {user?.lastName}</p>
                        <p className="text-sm text-gray-500 mb-2">{user?.email}</p>
                        <p className="text-xs text-gray-400">
                          {t('settings.clickAvatarToChange') || 'Click on avatar to change your profile picture'}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          {t('settings.maxFileSize') || 'Max file size: 5MB. JPG, PNG or GIF.'}
                        </p>
                      </div>
                    </div>
                  </CardBody>
                </Card>

                {/* Personal Information */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <UserIcon className="w-5 h-5 text-primary" />
                      {t('settings.personalInformation') || 'Personal Information'}
                    </CardTitle>
                  </CardHeader>
                  <CardBody>
                    <form onSubmit={handleProfileSubmit(onProfileSubmit)} className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Input
                          label={t('auth.firstName')}
                          error={profileErrors.firstName?.message}
                          {...registerProfile('firstName', { required: t('errors.required') })}
                        />
                        <Input
                          label={t('auth.lastName')}
                          error={profileErrors.lastName?.message}
                          {...registerProfile('lastName', { required: t('errors.required') })}
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Controller
                          name="phoneCountryCode"
                          control={control}
                          render={({ field }) => (
                            <Select
                              label={t('settings.countryCode') || 'Country Code'}
                              options={phoneCountryCodeOptions}
                              {...field}
                            />
                          )}
                        />
                        <div className="md:col-span-2">
                          <Input
                            label={t('common.phone')}
                            placeholder={t('settings.phonePlaceholder') || 'Enter phone number'}
                            {...registerProfile('phone')}
                          />
                        </div>
                      </div>

                      <div className="flex justify-end">
                        <Button type="submit" loading={loading}>
                          {t('settings.updateProfile')}
                        </Button>
                      </div>
                    </form>
                  </CardBody>
                </Card>

                {/* Preferences */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <GlobeAltIcon className="w-5 h-5 text-primary" />
                      {t('settings.preferences') || 'Preferences'}
                    </CardTitle>
                  </CardHeader>
                  <CardBody>
                    <form onSubmit={handleProfileSubmit(onProfileSubmit)} className="space-y-4">
                      <Controller
                        name="language"
                        control={control}
                        render={({ field }) => (
                          <Select
                            label={t('settings.language')}
                            options={languageOptions}
                            {...field}
                          />
                        )}
                      />

                      <Controller
                        name="timezone"
                        control={control}
                        render={({ field }) => (
                          <Select
                            label={t('settings.timezone')}
                            options={timezones}
                            {...field}
                          />
                        )}
                      />

                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <InformationCircleIcon className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                          <div className="text-sm text-blue-800">
                            <p className="font-medium mb-1">{t('settings.currentTime') || 'Current Time'}</p>
                            <p className="text-blue-700">
                              {dayjs().tz(user?.timezone || 'UTC').format('dddd, MMMM D, YYYY [at] h:mm A z')}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="flex justify-end">
                        <Button type="submit" loading={loading}>
                          {t('settings.updatePreferences') || 'Update Preferences'}
                        </Button>
                      </div>
                    </form>
                  </CardBody>
                </Card>
              </div>
            </TabPanel>

            {/* Account Tab */}
            <TabPanel active={activeTab === 'account'}>
              <div className="space-y-6">
                {/* Account Information */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <EnvelopeIcon className="w-5 h-5 text-primary" />
                      {t('settings.accountInformation') || 'Account Information'}
                    </CardTitle>
                  </CardHeader>
                  <CardBody>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between py-3 border-b border-gray-100">
                        <div>
                          <p className="text-sm text-gray-500">{t('common.email')}</p>
                          <p className="font-medium">{user?.email}</p>
                        </div>
                        {user?.isEmailVerified ? (
                          <Badge variant="success" className="flex items-center gap-1">
                            <CheckCircleIcon className="w-4 h-4" />
                            {t('settings.verified') || 'Verified'}
                          </Badge>
                        ) : (
                          <Badge variant="warning" className="flex items-center gap-1">
                            <XMarkIcon className="w-4 h-4" />
                            {t('settings.unverified') || 'Unverified'}
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center justify-between py-3 border-b border-gray-100">
                        <div>
                          <p className="text-sm text-gray-500">{t('settings.memberSince') || 'Member Since'}</p>
                          <p className="font-medium">
                            {user?.createdAt ? dayjs(user.createdAt).format('MMMM D, YYYY') : '-'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between py-3">
                        <div>
                          <p className="text-sm text-gray-500">{t('settings.lastLogin') || 'Last Login'}</p>
                          <p className="font-medium">
                            {user?.lastLogin ? dayjs(user.lastLogin).format('MMMM D, YYYY [at] h:mm A') : t('settings.never') || 'Never'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardBody>
                </Card>

                {/* Change Password */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <KeyIcon className="w-5 h-5 text-primary" />
                      {t('settings.changePassword')}
                    </CardTitle>
                  </CardHeader>
                  <CardBody>
                    <form onSubmit={handlePasswordSubmit(onPasswordSubmit)} className="space-y-4">
                      <Input
                        label={t('settings.currentPassword')}
                        type="password"
                        error={passwordErrors.currentPassword?.message}
                        {...registerPassword('currentPassword', { required: t('errors.required') })}
                      />

                      <Input
                        label={t('settings.newPassword')}
                        type="password"
                        error={passwordErrors.newPassword?.message}
                        {...registerPassword('newPassword', {
                          required: t('errors.required'),
                          minLength: { value: 8, message: t('errors.minLength', { min: 8 }) }
                        })}
                      />

                      <Input
                        label={t('auth.confirmPassword')}
                        type="password"
                        error={passwordErrors.confirmPassword?.message}
                        {...registerPassword('confirmPassword', {
                          required: t('errors.required'),
                          validate: value => value === newPassword || t('auth.passwordMismatch')
                        })}
                      />

                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <p className="text-sm text-gray-600">
                          {t('settings.passwordRequirements') || 'Password must be at least 8 characters long and contain a mix of letters and numbers.'}
                        </p>
                      </div>

                      <div className="flex justify-end">
                        <Button type="submit" loading={loading}>
                          {t('settings.changePassword')}
                        </Button>
                      </div>
                    </form>
                  </CardBody>
                </Card>
              </div>
            </TabPanel>

            {/* Notifications Tab */}
            <TabPanel active={activeTab === 'notifications'}>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BellIcon className="w-5 h-5 text-primary" />
                    {t('settings.notifications')}
                  </CardTitle>
                </CardHeader>
                <CardBody>
                  <div className="space-y-6">
                    {/* Email Notifications */}
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                        <EnvelopeIcon className="w-4 h-4" />
                        {t('settings.emailNotifications')}
                      </h3>
                      <div className="space-y-4">
                        <label className="flex items-start justify-between p-4 border border-gray-200 rounded-lg hover:border-primary transition-colors cursor-pointer">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium">{t('settings.emailNotifications')}</span>
                            </div>
                            <p className="text-sm text-gray-500">
                              {t('settings.emailNotificationsDesc') || 'Receive email notifications for important updates'}
                            </p>
                          </div>
                          <input
                            type="checkbox"
                            checked={notificationPrefs.email}
                            onChange={(e) => handleNotificationChange('email', e.target.checked)}
                            disabled={notificationLoading}
                            className="w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary"
                          />
                        </label>
                      </div>
                    </div>

                    {/* Notification Types */}
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 mb-4">
                        {t('settings.notificationTypes') || 'Notification Types'}
                      </h3>
                      <div className="space-y-4">
                        <label className="flex items-start justify-between p-4 border border-gray-200 rounded-lg hover:border-primary transition-colors cursor-pointer">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium">{t('settings.projectUpdates')}</span>
                            </div>
                            <p className="text-sm text-gray-500">
                              {t('settings.projectUpdatesDesc') || 'Get notified when projects are updated, milestones are reached, or deadlines change'}
                            </p>
                          </div>
                          <input
                            type="checkbox"
                            checked={notificationPrefs.projectUpdates}
                            onChange={(e) => handleNotificationChange('projectUpdates', e.target.checked)}
                            disabled={notificationLoading}
                            className="w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary"
                          />
                        </label>

                        <label className="flex items-start justify-between p-4 border border-gray-200 rounded-lg hover:border-primary transition-colors cursor-pointer">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium">{t('settings.taskReminders')}</span>
                            </div>
                            <p className="text-sm text-gray-500">
                              {t('settings.taskRemindersDesc') || 'Receive reminders for upcoming task deadlines and assignments'}
                            </p>
                          </div>
                          <input
                            type="checkbox"
                            checked={notificationPrefs.taskReminders}
                            onChange={(e) => handleNotificationChange('taskReminders', e.target.checked)}
                            disabled={notificationLoading}
                            className="w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary"
                          />
                        </label>

                        <label className="flex items-start justify-between p-4 border border-gray-200 rounded-lg hover:border-primary transition-colors cursor-pointer">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium">{t('settings.clientMessages')}</span>
                            </div>
                            <p className="text-sm text-gray-500">
                              {t('settings.clientMessagesDesc') || 'Get notified when clients send messages or comments'}
                            </p>
                          </div>
                          <input
                            type="checkbox"
                            checked={notificationPrefs.clientMessages}
                            onChange={(e) => handleNotificationChange('clientMessages', e.target.checked)}
                            disabled={notificationLoading}
                            className="w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary"
                          />
                        </label>
                      </div>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </TabPanel>

            {/* Security Tab */}
            <TabPanel active={activeTab === 'security'}>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ShieldCheckIcon className="w-5 h-5 text-primary" />
                    {t('settings.security') || 'Security'}
                  </CardTitle>
                </CardHeader>
                <CardBody>
                  <div className="space-y-6">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <InformationCircleIcon className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-blue-800">
                          <p className="font-medium mb-1">{t('settings.securityTips') || 'Security Tips'}</p>
                          <ul className="list-disc list-inside space-y-1 text-blue-700">
                            <li>{t('settings.useStrongPassword') || 'Use a strong, unique password'}</li>
                            <li>{t('settings.changePasswordRegularly') || 'Change your password regularly'}</li>
                            <li>{t('settings.dontShareCredentials') || 'Never share your login credentials'}</li>
                            <li>{t('settings.logoutFromSharedDevices') || 'Always logout from shared devices'}</li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                        <div>
                          <p className="font-medium">{t('settings.twoFactorAuth') || 'Two-Factor Authentication'}</p>
                          <p className="text-sm text-gray-500">
                            {t('settings.twoFactorAuthDesc') || 'Add an extra layer of security to your account'}
                          </p>
                        </div>
                        <Badge variant="neutral">{t('settings.comingSoon') || 'Coming Soon'}</Badge>
                      </div>

                      <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                        <div>
                          <p className="font-medium">{t('settings.activeSessions') || 'Active Sessions'}</p>
                          <p className="text-sm text-gray-500">
                            {t('settings.activeSessionsDesc') || 'View and manage your active login sessions'}
                          </p>
                        </div>
                        <Badge variant="neutral">{t('settings.comingSoon') || 'Coming Soon'}</Badge>
                      </div>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </div>
    </>
  );
}